import { useEffect, useState } from 'react';
import type { AxiosResponse } from 'axios';
import api, { BROWSER_SESSION_EXPIRED_EVENT, BROWSER_SESSION_ROTATED_EVENT } from '../services/api.ts';
import { getPlatformAvailabilityIdentity } from '../services/platformAvailability.ts';
import { getSafeHttpsUrl } from '../utils/safeUrl.ts';
import { isMediaExpiredMarker } from '../utils/mediaRetention.ts';
import type { TemplateComponent } from '../types/templateMessage.ts';

const PRIVATE_HOST = /^(localhost|127\.|10\.|0\.|169\.254\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.|\[|::)|\.(localhost|local|internal|test)$/i;
const SECRET_QUERY = /^(access_token|token|authorization|auth|signature|credential|key|api_key|apikey|policy|key-pair-id|awsaccesskeyid|x-amz-|x-goog-|x-auth-)|(?:_|-)token$/i;

/** Snapshot links must not expose provider credentials or private media hosts. */
export const safeTemplateUrl = (value: unknown, media = false): string | null => {
  let safe = getSafeHttpsUrl(value);
  if (!safe && !media && typeof value === 'string' && /^http:\/\//i.test(value.trim())) {
    const candidate = value.trim();
    if (getSafeHttpsUrl(candidate.replace(/^http:/i, 'https:'))) safe = candidate;
  }
  if (!safe || /\{\{.*\}\}/.test(safe)) return null;
  const parsed = new URL(safe);
  if (media && PRIVATE_HOST.test(parsed.hostname)) return null;
  for (const [key] of Array.from(parsed.searchParams.entries())) {
    if (SECRET_QUERY.test(key)) return null;
  }
  for (const [key] of Array.from(new URLSearchParams(parsed.hash.slice(1)).entries())) {
    if (SECRET_QUERY.test(key)) return null;
  }
  return safe;
};

type MediaKind = 'image' | 'video' | 'document';
type MediaState = { key: string; status: 'loading' | 'ready' | 'unavailable'; src: string | null };
const SCOPE_KEYS = ['user_type', 'user_id', 'client_id', 'clinic_id'];
const currentMediaScope = () => {
  try { return `${getPlatformAvailabilityIdentity()}|${SCOPE_KEYS.map(key => localStorage.getItem(key) || '').join(':')}`; }
  catch { return getPlatformAvailabilityIdentity(); }
};

export const useTemplateMessageMedia = (
  media: TemplateComponent['media'],
  kind: MediaKind,
  databaseMessageId: number | null | undefined,
  componentPath: string,
) => {
  const [scopeRevision, setScopeRevision] = useState(0);
  const [sessionExpired, setSessionExpired] = useState(false);
  useEffect(() => {
    const rotated = () => { setSessionExpired(false); setScopeRevision(value => value + 1); };
    const ended = () => { setSessionExpired(true); setScopeRevision(value => value + 1); };
    const storage = (event: StorageEvent) => { if (event.key === null || SCOPE_KEYS.includes(event.key)) rotated(); };
    window.addEventListener(BROWSER_SESSION_ROTATED_EVENT, rotated);
    window.addEventListener(BROWSER_SESSION_EXPIRED_EVENT, ended);
    window.addEventListener('storage', storage);
    return () => {
      window.removeEventListener(BROWSER_SESSION_ROTATED_EVENT, rotated);
      window.removeEventListener(BROWSER_SESSION_EXPIRED_EVENT, ended);
      window.removeEventListener('storage', storage);
    };
  }, []);
  const scope = currentMediaScope();
  const link = safeTemplateUrl(media?.link, true);
  const hasId = typeof media?.id === 'string' && /^\d{1,255}$/.test(media.id);
  const validMessageId = Number.isSafeInteger(databaseMessageId) && Number(databaseMessageId) > 0;
  const validPath = /^\d{1,3}(?:\.\d{1,3}\.\d{1,3}){0,4}$/.test(componentPath);
  const expired = media?.expired === true || isMediaExpiredMarker((media as Record<string, unknown> | undefined)?.marker) || isMediaExpiredMarker(media?.link) || sessionExpired;
  const key = `${scope}|${scopeRevision}|${databaseMessageId ?? ''}|${componentPath}|${kind}|${media?.id ?? ''}|${link ?? ''}|${expired}`;
  const initialStatus = expired ? 'unavailable' : link ? 'ready' : hasId && validMessageId && validPath ? 'loading' : 'unavailable';
  const [state, setState] = useState<MediaState>({ key, status: initialStatus, src: expired ? null : link });

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    const controller = new AbortController();
    setState({ key, status: initialStatus, src: expired ? null : link });
    if (initialStatus !== 'loading') return () => controller.abort();

    api.get(`/api/chat-optimized/messages/${databaseMessageId}/template-media`, {
      params: { component_path: componentPath }, responseType: 'blob',
      timeout: 15000, signal: controller.signal,
    }).then((response: AxiosResponse<Blob>) => {
      if (cancelled || currentMediaScope() !== scope) return;
      const data = response.data;
      if (!(data instanceof Blob) || !data.size) throw new Error('Mídia indisponível');
      const mime = (data.type || response.headers?.['content-type'] || '').split(';')[0].toLowerCase();
      if ((kind === 'image' && !/^image\/(jpeg|png|gif|webp|avif|bmp|heic|heif)$/.test(mime)) ||
          (kind === 'video' && !/^video\/(mp4|webm|ogg|quicktime|3gpp|x-m4v)$/.test(mime))) {
        throw new Error('Mídia indisponível');
      }
      // Documents are downloadable attachments, never active embedded content.
      objectUrl = URL.createObjectURL(kind === 'document' ? new Blob([data], { type: 'application/octet-stream' }) : data);
      setState({ key, status: 'ready', src: objectUrl });
    }).catch(() => {
      if (!cancelled) setState({ key, status: 'unavailable', src: null });
    });
    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [componentPath, databaseMessageId, expired, initialStatus, key, kind, link, scope]);

  return {
    ...(state.key === key ? state : { key, status: initialStatus, src: expired ? null : link }),
    markUnavailable: () => setState({ key, status: 'unavailable', src: null }),
  };
};
